import React, { useEffect, useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { faPlus, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { confirmSetupIntent } from "./api";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { setError, setLoading } from "../../../../redux/actions/app";
import { tt } from "../../../../utils/i18n/i18n";
import "./styles.scss";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";

interface AddCardFormProps {
  businessUserId: string;
  setupIntentId: string;
  onSuccess: () => void;
  onCancel?: () => void;
  hideCancel?: boolean;
  hideSubtitle?: boolean;
}

export const AddCardForm: React.FC<AddCardFormProps> = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name:
                accountToDisplay?.placeName ||
                accountToDisplay?.email ||
                accountToDisplay?.trackingID
            }
          }
        },
        redirect: "if_required"
      });

      if (error) {
        dispatch(setError(error.message || tt("api_error")));
      } else {
        await confirmSetupIntent(props.setupIntentId, props.businessUserId);
        props.onSuccess();
      }
    } catch (err: any) {
      dispatch(setError(err?.message || tt("api_error")));
    } finally {
      setIsLoading(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading]);

  return (
    <div className="p-4 add-card-form kc-card-shadow">
      <div className="add-card-header">
        {!props.hideSubtitle && <KC_Heading3 text={tt("add_new_card")} />}
        {!props.hideCancel && props.onCancel && (
          <KC_Button
            text={tt("cancel")}
            size={KC_ButtonSize.Large}
            onClick={props.onCancel}
            isPrimary={true}
            disabled={isLoading}
            extraStyle={{
              width: "unset !important"
            }}
          />
        )}
      </div>
      <form onSubmit={handleSubmit}>
        <div className="payment-element-container">
          <PaymentElement
            options={{
              layout: "tabs",
              fields: {
                billingDetails: "auto"
              }
            }}
          />
        </div>

        <div className="form-actions">
          <KC_Button
            size={KC_ButtonSize.Large}
            text={isLoading ? tt("saving") : tt("add_card")}
            type="submit"
            disabled={!stripe || isLoading}
            showIconOnTheLeft={true}
            icon={isLoading ? faSpinner : faPlus}
            spinIcon={isLoading}
            extraStyle={{
              width: "unset !important"
            }}
          />
        </div>
      </form>
    </div>
  );
};
